/**
 * Description: Handles customization logic for the frame product.
 * Author: Milo
 */

if (!customElements.get('sa-customize-main')) {
  class SaCustomizeMain extends HTMLElement {
    constructor() {
      super();
      this.state = {
        finish: null,
        colorType: null,
        colorSelections: {}, // { 'Color 1': 'Red', 'Color 2': 'Blue' }
        logo: null,
        logoColor: null,
        parentId: null,
        parentVariantId: null,
        file: null,
        description: ''
      };

      this.handleOptionChange = this.handleOptionChange.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
    }

    connectedCallback() {
      this.initData();
      this.initUrlParams();
      this.initElements();
      this.initEvents();
      this.initCustomSelect();
      this.initStyleCarousel();
      this.initScrollPosition();
      this.setDefaultSelections();
      this.updateInitialPrice();
      this.updatePreview();
      this.initStickyObserver();
      this.initMobileScrollBehavior();
    }

    disconnectedCallback() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
    }

    initMobileScrollBehavior() {
      let lastScrollTop = 0;
      const threshold = 10;
      const preview = this.querySelector('.sa-preview');
      const bar = this.querySelector('.sa-bar');

      if (!preview || !bar) return;

      const handleScroll = () => {
        const scrollTop = window.scrollY;
        
        // Ignore bounce effect (negative scroll)
        if (scrollTop < 0) return;
        
        // Always show if at top (e.g. within 50px)
        if (scrollTop < 50) {
           preview.classList.remove('sa-scroll-hidden');
           bar.classList.remove('sa-scroll-hidden');
           lastScrollTop = scrollTop;
           return;
        }

        if (Math.abs(scrollTop - lastScrollTop) < threshold) return;

        const isScrollDown = scrollTop > lastScrollTop;
        const isMobile = window.innerWidth < 768;

        if (isScrollDown) {
           // Scroll Down -> Hide
           if (isMobile) {
             preview.classList.add('sa-scroll-hidden');
           }
           // Always hide bar on scroll down (Mobile & Desktop)
           bar.classList.add('sa-scroll-hidden');
        } else {
           // Scroll Up -> Show
           preview.classList.remove('sa-scroll-hidden');
           bar.classList.remove('sa-scroll-hidden');
        }
        
        lastScrollTop = scrollTop;
      };

      // Simple throttle
      const throttle = (func, limit) => {
        let inThrottle;
        return function() {
          const args = arguments;
          const context = this;
          if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
          }
        }
      }

      this.scrollHandler = throttle(handleScroll, 100);
      window.addEventListener('scroll', this.scrollHandler);
    }

    showMobileStickyElements() {
      const preview = this.querySelector('.sa-preview');
      if (preview) preview.classList.remove('sa-scroll-hidden');
    }

    initStickyObserver() {
      const sentinel = this.querySelector('#sa-preview-sentinel');
      const preview = this.querySelector('.sa-preview');
      
      if (!sentinel || !preview) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            preview.classList.add('is-stuck');
          } else {
            preview.classList.remove('is-stuck');
          }
        });
      }, {
        threshold: 0
      });
      
      observer.observe(sentinel);
    }

    initStyleCarousel() {
      const wrapper = this.querySelector('.sa-style-picker-wrapper');
      if (!wrapper) return;

      const grid = wrapper.querySelector('.sa-customize-style-picker__grid');
      const prevBtn = wrapper.querySelector('.sa-style-nav-btn--prev');
      const nextBtn = wrapper.querySelector('.sa-style-nav-btn--next');

      if (!grid || !prevBtn || !nextBtn) return;

      const updateButtons = () => {
        const { scrollLeft, scrollWidth, clientWidth } = grid;
        // Tolerance for float calculation issues
        const isAtStart = scrollLeft <= 1; // tolerance 1px
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

        if (isAtStart) prevBtn.classList.add('hidden');
        else prevBtn.classList.remove('hidden');

        if (isAtEnd) nextBtn.classList.add('hidden');
        else nextBtn.classList.remove('hidden');
        
        // Hide both if not scrollable
        if (scrollWidth <= clientWidth) {
           prevBtn.classList.add('hidden');
           nextBtn.classList.add('hidden');
        }
      };

      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Scroll width of one card (approx 160 + gap 16 = 176) or just a fixed amount
        // grid.scrollBy({ left: -200, behavior: 'smooth' });
        // Let's scroll by container width / 2 for better UX
        const scrollAmount = grid.clientWidth * 0.5;
        grid.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const scrollAmount = grid.clientWidth * 0.5;
        grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });

      grid.addEventListener('scroll', () => {
        // Debounce could be good, but direct update is responsive
        updateButtons();
      });

      // Initial check
      // Use requestAnimationFrame to ensure layout is computed
      requestAnimationFrame(() => {
          updateButtons();
      });

      // Update on resize
      window.addEventListener('resize', updateButtons);
    }

    initScrollPosition() {
      const activeCard = this.querySelector('.sa-customize-style-picker__card--active');
      const grid = this.querySelector('.sa-customize-style-picker__grid');

      if (activeCard && grid) {
        // Use setTimeout to ensure the layout is ready
        setTimeout(() => {
          // Manual scroll calculation to avoid page vertical scrolling
          // With position: relative on grid, offsetLeft is relative to grid content start
          const scrollLeft = activeCard.offsetLeft - (grid.clientWidth / 2) + (activeCard.clientWidth / 2);

          grid.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        }, 100);
      }
    }

    initData() {
      const dataScript = this.querySelector('[data-customize-data]');
      try {
        this.productData = JSON.parse(dataScript.textContent);

        // Resolve current style data based on Liquid's decision if available, 
        // otherwise fall back to variant matching or first style.
        if (this.productData.styles) {
          let styleData = null;

          // 1. Try explicit currentStyle passed from Liquid
          if (this.productData.currentStyle) {
            styleData = this.productData.styles.find(s => s.name === this.productData.currentStyle);
          }

          // 2. If not found, try matching via variant options (Legacy/Fallback)
          if (!styleData && this.productData.variants) {
            const currentVariant = this.productData.variants.find(v => v.id === this.productData.variantId);
            if (currentVariant) {
              const styleIndex = this.productData.options.findIndex(o => {
                const name = o.toLowerCase();
                return name === 'customize style' || name === 'style';
              });
              if (styleIndex > -1) {
                const styleName = currentVariant.options[styleIndex];
                styleData = this.productData.styles.find(s => s.name === styleName);
              }
            }
          }

          // 3. Fallback to first style
          if (!styleData && this.productData.styles.length > 0) {
            styleData = this.productData.styles[0];
          }

          if (styleData) {
            Object.assign(this.productData, styleData);
          }
        }

      } catch (e) {
        console.error('Failed to parse customize data', e);
        this.productData = {};
      }
    }

    initUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      this.state.parentVariantId = urlParams.get('parent_variant_id');
      
      // Extract Addon Items (Base64 JSON)
      const addonItemsParam = urlParams.get('addon_items');
      try {
        if (addonItemsParam) {
           // UTF-8 safe Base64 decoding
           const jsonStr = decodeURIComponent(escape(atob(addonItemsParam)));
           this.state.addonItems = JSON.parse(jsonStr);
        } else {
           this.state.addonItems = [];
        }
      } catch (e) {
        // Fallback for backward compatibility (if plain JSON) or error
        try {
            this.state.addonItems = addonItemsParam ? JSON.parse(addonItemsParam) : [];
        } catch (e2) {
            console.error('Failed to parse addon_items', e2);
            this.state.addonItems = [];
        }
      }

      // Extract Extra Properties (exclude known params)
      this.state.extraProperties = {};
      const excludedParams = ['parent_variant_id', 'addon_items', 'view', 'variant'];
      
      for (const [key, value] of urlParams.entries()) {
        if (!excludedParams.includes(key)) {
            // Clean up property name if it has brackets (e.g. properties[Size] -> Size)
            let propName = key;
            if (propName.startsWith('properties[')) {
                propName = propName.replace('properties[', '').replace(']', '');
            }
            // Decode value just in case
            this.state.extraProperties[propName] = value;
        }
      }

      // If no parent variant, we might want to alert or redirect, but for now we proceed
    }

    initElements() {
      this.previewContainer = this.querySelector('.sa-preview-container');
      this.finishOptions = this.querySelectorAll('[name="customize_finish"]');
      this.colorTypeOptions = this.querySelectorAll('[name="customize_color_type"]');
      this.logoOptions = this.querySelectorAll('[name="customize_logo"]');
      this.addToCartBtn = this.querySelector('.sa-add-to-cart');
      this.priceTexts = this.querySelectorAll('.sa-price-text');

      // Color container needs to be dynamic
      this.colorConfigContainer = this.querySelector('.sa-color-config-container');
      this.logoConfigContainer = this.querySelector('.sa-logo-config-container');
    }

    initEvents() {
      this.addEventListener('change', this.handleOptionChange);
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', this.handleAddToCart);
      }
      this.initFileUpload();
    }

    initFileUpload() {
      const dropZone = this.querySelector('#sa-file-upload-zone');
      const fileInput = this.querySelector('#customize_references');
      const removeBtn = this.querySelector('.sa-file-remove');
      const textarea = this.querySelector('#customize_description');

      if (dropZone && fileInput) {
        dropZone.addEventListener('click', (e) => {
          if (e.target.closest('.sa-file-remove')) return;
          fileInput.click();
        });

        dropZone.addEventListener('dragover', (e) => {
          e.preventDefault();
          dropZone.classList.add('drag-over');
        });

        dropZone.addEventListener('dragleave', () => {
          dropZone.classList.remove('drag-over');
        });

        dropZone.addEventListener('drop', (e) => {
          e.preventDefault();
          dropZone.classList.remove('drag-over');
          if (e.dataTransfer.files.length) {
            this.handleFileSelect(e.dataTransfer.files[0]);
          }
        });

        fileInput.addEventListener('change', (e) => {
          if (fileInput.files.length) {
            this.handleFileSelect(fileInput.files[0]);
          }
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.state.file = null;
          if (fileInput) fileInput.value = '';
          const preview = this.querySelector('.sa-file-preview');
          const content = this.querySelector('.sa-file-upload-content');
          if (preview) preview.classList.add('hidden');
          if (content) content.classList.remove('hidden');
        });
      }

      if (textarea) {
        textarea.addEventListener('input', (e) => {
          this.state.description = e.target.value;
        });
      }
    }

    handleFileSelect(file) {
      if (!file) return;

      // Validate file type
      const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload SVG, PNG, JPG or GIF.');
        return;
      }

      this.state.file = file;

      // Update UI
      const preview = this.querySelector('.sa-file-preview');
      const content = this.querySelector('.sa-file-upload-content');
      const nameEl = this.querySelector('.sa-file-name');

      if (preview && content && nameEl) {
        nameEl.textContent = file.name;
        preview.classList.remove('hidden');
        content.classList.add('hidden');
      }
    }

    initCustomSelect() {
      const selectContainer = this.querySelector('.sa-select-container');
      if (!selectContainer) return;

      const trigger = selectContainer.querySelector('.sa-select-trigger');
      const optionsContainer = selectContainer.querySelector('.sa-select-options');
      const options = selectContainer.querySelectorAll('.sa-select-option');

      // Toggle dropdown
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        optionsContainer.classList.toggle('open');
      });

      // Close when clicking outside
      document.addEventListener('click', (e) => {
        if (!selectContainer.contains(e.target)) {
          optionsContainer.classList.remove('open');
        }
      });

      // Option selection
      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = option.dataset.value;

          // Close dropdown
          optionsContainer.classList.remove('open');

          // Trigger hidden radio change
          const radio = this.querySelector(`input[name="customize_color_type"][value="${value}"]`);
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    setDefaultSelections() {
      // Default Finish
      if (this.productData.customize_finish && this.productData.customize_finish.length > 0) {
        // Ensure we select the first finish by default
        this.selectOption('finish', this.productData.customize_finish[0].name, false);
      }

      // Default Logo
      if (this.productData.customize_logo && this.productData.customize_logo.length > 0) {
        this.selectOption('logo', this.productData.customize_logo[0].name, false);
      }

      // Default Color Type
      if (this.productData.customize_color_type && this.productData.customize_color_type.length > 0) {
        this.selectOption('colorType', this.productData.customize_color_type[0].label, false);
      }
    }

    handleOptionChange(e) {
      const target = e.target;
      const type = target.dataset.type;
      const value = target.value;

      if (!type) return;

      // Show sticky elements when any option changes
      this.showMobileStickyElements();

      if (type === 'finish') {
        // Check for unavailable colors
        const unavailable = this.checkFinishAvailability(value);
        if (unavailable.length > 0) {
           this.showFinishWarningModal(unavailable, value);
           return;
        }
        this.selectOption('finish', value);
      } else if (type === 'style') {
        this.handleStyleChange(value);
      } else if (type === 'colorType') {
        this.selectOption('colorType', value);
      } else if (type === 'colorSelection') {
        const slot = target.dataset.slot; // e.g., "Color 1"
        this.selectColorForSlot(slot, value, target);
      } else if (type === 'logo') {
        this.selectOption('logo', value);
      } else if (type === 'logoColor') {
        this.selectOption('logoColor', value);
      }
    }

    handleStyleChange(styleValue) {
      if (!this.productData.options || !this.productData.variants) return;

      const styleIndex = this.productData.options.findIndex(o => {
        const name = o.toLowerCase();
        return name === 'customize style' || name === 'style';
      });
      if (styleIndex === -1) return;

      const variant = this.productData.variants.find(v => v.options[styleIndex] === styleValue);

      if (variant) {
        // Update productData variantId
        this.productData.variantId = variant.id;
        this.state.variantId = variant.id;

        // Update Price
        if (this.priceTexts && variant.price_formatted) {
          this.priceTexts.forEach(el => el.innerHTML = variant.price_formatted);
        }

        // Update Add to Cart Button
        if (this.addToCartBtn) {
          if (variant.available) {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.textContent = 'Add to cart';
          } else {
            this.addToCartBtn.disabled = true;
            this.addToCartBtn.textContent = 'Sold out';
          }
        }

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('variant', variant.id);
        const newUrl = `${window.location.pathname}?${currentParams.toString()}`;

        // Update URL without reload
        window.history.replaceState({ path: newUrl }, '', newUrl);

        // Instant Client-Side Update (No Fetch)
        // 1. Update productData to match the new style
        const newStyleData = this.productData.styles.find(s => s.name === styleValue);
        if (newStyleData) {
          Object.assign(this.productData, newStyleData);

          // Update Style Description
          this.updateStyleDescription(newStyleData.description);
          
          // Clear fully custom state if not customizable
          if (!this.productData.customizable) {
             this.state.file = null;
             this.state.description = '';
          }
        }

        // Update Style Picker UI
        this.updateUI('style', styleValue);

        // Scroll active card into view
        this.initScrollPosition();

        // 2. Render the Pickers via JS
        this.renderFinishPicker();

        // 3. Re-initialize state
        this.setDefaultSelections();
        this.updatePreview();
      }
    }

    renderFinishPicker() {
      // Note: Logic below assumes we rebuild dynamic parts. If DOM is missing, we create it.

      const optionsContainer = this.querySelector('.sa-options');
      if (!optionsContainer) return;

      // Find style section to anchor
      const sections = Array.from(optionsContainer.querySelectorAll('.sa-section'));
      const styleSection = sections[0]; // First one is style picker
      if (!styleSection) return;

      // Remove all subsequent siblings (old pickers)
      let next = styleSection.nextElementSibling;
      while (next) {
        const toRemove = next;
        next = next.nextElementSibling;
        toRemove.remove();
      }

      // Remove "Customize logo" title if it exists as a direct child p tag
      const logoTitle = optionsContainer.querySelector('.sa-title:not(:first-child)');
      if (logoTitle) logoTitle.remove();

      // --- Re-render Fully Picker (File Upload) HTML ---
      let fullyPickerHtml = '';
      if (this.productData.customizable) {
        const uploadIcon = this.dataset.uploadIcon;
        fullyPickerHtml = `
<div class="sa-section sa-customize-fully-picker">
  <div class="sa-form-group">
    <p class="sa-section-title">Upload paint references:</p>
    <div class="sa-file-upload" id="sa-file-upload-zone">
      <input type="file" id="customize_references" name="customize_references" accept=".svg, .png, .jpg, .jpeg, .gif" class="sa-file-input" hidden>
      <div class="sa-file-upload-content">
        <div class="sa-file-icon">
          <img src="${uploadIcon}" alt="Upload">
        </div>
        <p class="sa-file-text">
          <span class="sa-file-link">Click to upload</span> <span class="sa-mobile-hidden">or drag and drop</span>
        </p>
        <p class="sa-file-hint">SVG, PNG, JPG or GIF (max. 800x400px)</p>
      </div>
      <div class="sa-file-preview hidden">
         <span class="sa-file-name"></span>
         <button type="button" class="sa-file-remove" aria-label="Remove file">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
         </button>
      </div>
    </div>
  </div>

  <div class="sa-form-group">
    <p class="sa-section-title">Describe your ideal frame (optional):</p>
    <textarea id="customize_description" name="customize_description" class="sa-textarea" rows="4"></textarea>
  </div>
</div>
        `;
      }

      // --- Re-render Finish Picker HTML ---
      let html = '';
      if (this.productData.customize_finish && this.productData.customize_finish.length > 1) {
        html += `
            <div class="sa-section">
              <p class="sa-section-title">Choose finish</p>
              <div class="sa-customize-finish-picker__grid">
            `;
        this.productData.customize_finish.forEach(finish => {
          html += `
                <label class="sa-customize-finish-picker__option">
                  <input type="radio" name="customize_finish" value="${finish.name}" data-type="finish" style="display:none">
                  ${finish.name}
                </label>
                `;
        });
        html += `</div></div>`;
      }

      // --- Re-render Color Picker HTML ---
      let colorHtml = '';
      if (this.productData.customize_color_type && this.productData.customize_color_type.length > 0) {
        const colorCount = this.productData.customize_color_type.length;
        colorHtml += `
          <div class="sa-section">
            <p class="sa-section-title">Choose color</p>
        `;

        if (colorCount > 1) {
          colorHtml += `<div class="sa-select-container">`;
          // Hidden inputs
          this.productData.customize_color_type.forEach((type, index) => {
            colorHtml += `<input type="radio" name="customize_color_type" value="${type.label}" data-type="colorType" style="display:none" ${index === 0 ? 'checked' : ''}>`;
          });

          // Custom Select
          const firstType = this.productData.customize_color_type[0];
          colorHtml += `
                <div class="sa-custom-select">
                  <div class="sa-select-trigger">
                    <div class="sa-select-value">
                      ${firstType.image ? `<img src="${firstType.image}" class="sa-select-icon" alt="${firstType.label}">` : ''}
                      <span class="sa-select-text">${firstType.label}</span>
                    </div>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </div>
                  <div class="sa-select-options">
                `;

          this.productData.customize_color_type.forEach((type, index) => {
            colorHtml += `
                    <div class="sa-select-option ${index === 0 ? 'selected' : ''}" data-value="${type.label}">
                       ${type.image ? `<img src="${type.image}" class="sa-select-icon" alt="${type.label}">` : ''}
                       <span class="sa-select-text">${type.label}</span>
                    </div>
                    `;
          });

          colorHtml += `</div></div></div>`;
        }

        colorHtml += `
          <div class="sa-color-config-container"></div>
          <p class="sa-preview-note sa-color-description-note hidden"></p>
        </div>`;
      }

      // --- Re-render Logo Picker HTML ---
      let logoHtml = '';
      if (this.productData.customize_logo && this.productData.customize_logo.length > 0) {
        logoHtml += `
              <p class="sa-title">Customize logo</p>
              <div class="sa-section">
                 <p class="sa-section-title">Choose type</p>
                 <div class="sa-logo-type-list">
            `;

        this.productData.customize_logo.forEach(logo => {
          logoHtml += `
                <label class="sa-option-card">
                  ${logo.image ? `<img src="${logo.image}" class="sa-option-image" alt="${logo.name}">` : ''}
                  <input type="radio" name="customize_logo" value="${logo.name}" data-type="logo" style="display:none">
                </label>
                `;
        });

        logoHtml += `
                 </div>
                 <div class="sa-header">
                    <p class="sa-section-title">Choose color</p>
                    <p class="sa-logo-color-name"></p>
                 </div>
                 <div class="sa-logo-config-container"></div>
              </div>
            `;
      }

      // Insert new HTML
      styleSection.insertAdjacentHTML('afterend', fullyPickerHtml + html + colorHtml + logoHtml);

      // Re-init references
      this.initElements();
      this.initCustomSelect();
      this.initFileUpload();
    }

    selectOption(type, value) {
      this.state[type] = value;

      // Update UI Active States
      this.updateUI(type, value);

      // Logic for cascaded updates
      if (type === 'finish') {
        let colorType = this.state.colorType;
        // Fallback if colorType is missing in state (e.g. Single Color mode where selector is hidden)
        if (!colorType && this.productData.customize_color_type && this.productData.customize_color_type.length > 0) {
          colorType = this.productData.customize_color_type[0].label;
          this.state.colorType = colorType;
        }

        if (colorType !== null && colorType !== undefined) {
          // this.state.colorSelections = {};
          this.renderColorSlots(colorType);
        }
      } else if (type === 'colorType') {
        // Reset selections 
        // when color type structure changes
        this.state.colorSelections = {};
        this.renderColorSlots(value);
      }
      if (type === 'logo') {
        this.renderLogoColors(value);
      } else if (type === 'logoColor') {
        const label = this.querySelector('.sa-logo-color-name');
        if (label) label.textContent = value;
      }

      this.updatePreview();
    }

    selectColorForSlot(slot, colorName, target, skipLogoUpdate = false) {
      this.state.colorSelections[slot] = colorName;

      // Update UI Active State
      this.updateUI('colorSelection', colorName);

      // Update UI for the Tab Content
      if (target) {
        const index = parseInt(target.dataset.index || '0');
        const colorValue = target.dataset.colorValue;
        const colorImage = target.dataset.colorImage;

        const previewEl = this.querySelector(`.sa-tab-preview-${index}`);
        const subtitleEl = this.querySelector(`.sa-tab-subtitle-${index}`);

        if (previewEl) {
          const imgEl = previewEl.querySelector('img');
          if (imgEl) {
            if (colorImage) {
              imgEl.src = colorImage;
              imgEl.style.display = 'block';
              previewEl.style.backgroundColor = 'transparent';
            } else {
              imgEl.style.display = 'none';
              previewEl.style.backgroundColor = colorValue;
            }
          }
        }
        if (subtitleEl) subtitleEl.textContent = colorName;

        // Auto-select Logo Color if this is the last slot
        // Instead of relying on typeData keys, we check if there is a next slot
        // If there is no next slot, then we are at the last slot.
        const nextSlotIndex = index + 1;
        const nextSlotExists = this.querySelector(`input[data-index="${nextSlotIndex}"]`);

        if (!nextSlotExists && !skipLogoUpdate) {
          const logoName = (target.dataset.logo || '').trim();
          if (logoName) {
            // Robust check: iterate all logo inputs to match value loosely
            const logoInputs = this.querySelectorAll('input[name="customize_logo_color"]');
            const match = Array.from(logoInputs).find(input => input.value === logoName);
            
            if (match) {
              this.selectOption('logoColor', match.value);
            }
          }
        }
      }

      this.updatePreview();

      // Update Color Description Visibility
      this.updateColorDescription();
    }

    updateColorDescription() {
      // Find active tab index
      const activeTab = this.querySelector('.sa-slot-tab--active');
      let index = 0;
      if (activeTab) {
        index = parseInt(activeTab.dataset.tabIndex || '0');
      }

      const slotName = `Color ${index + 1}`;
      const colorName = this.state.colorSelections[slotName];
      const noteEl = this.querySelector('.sa-color-description-note');
      
      if (noteEl) {
        let description = '';
        
        if (colorName) {
           // Find color data
           const slotColors = this.productData.customize_color_slots && this.productData.customize_color_slots[index];
           if (slotColors) {
             const colorData = slotColors.find(c => c.name === colorName);
             if (colorData && colorData.colorful && colorData.colorful.description) {
               description = colorData.colorful.description;
             }
           }
        }

        if (description) {
          noteEl.textContent = description;
          noteEl.classList.remove('hidden');
        } else {
          noteEl.classList.add('hidden');
        }
      }
    }

    updateInitialPrice() {
      if (!this.productData.variantId || !this.productData.variants) return;
      const variant = this.productData.variants.find(v => v.id === this.productData.variantId);
      
      if (variant) {
         // Update Price Text
         if (this.priceTexts && variant.price_formatted) {
            this.priceTexts.forEach(el => el.innerHTML = variant.price_formatted);
         }
         
         // Update Add to Cart Button Status
         if (this.addToCartBtn) {
            if (variant.available) {
              this.addToCartBtn.disabled = false;
              this.addToCartBtn.textContent = 'Add to cart';
            } else {
              this.addToCartBtn.disabled = true;
              this.addToCartBtn.textContent = 'Sold out';
            }
         }
      }
    }

    updateStyleDescription(description) {
      const container = this.querySelector('.sa-style-description');
      const note = container ? container.querySelector('.sa-preview-note') : null;
      
      if (container && note) {
        if (description) {
          note.textContent = description;
          container.classList.remove('hidden');
        } else {
          container.classList.add('hidden');
        }
      }
    }

    updateUI(type, value) {
      // Generic active class toggling
      const inputs = this.querySelectorAll(`[data-type="${type}"]`);
      inputs.forEach(input => {
        const card = input.closest('.sa-option-card') ||
          input.closest('.sa-customize-finish-picker__option') ||
          input.closest('.sa-swatch') ||
          input.closest('.sa-customize-style-picker__card'); // Added style card

        // For colorSelection, ensure we only toggle for the specific slot
        if (type === 'colorSelection') {
          const slot = input.dataset.slot;
        }

        if (card) {
          // Modified check for colorSelection to ensure slot matching
          let isActive = input.value === value;

          if (type === 'colorSelection') {
            // We need to check if this input's value matches the selected value FOR ITS SLOT
            const slot = input.dataset.slot;
            isActive = this.state.colorSelections[slot] === input.value;
          }

          if (isActive) {
            input.checked = true;
            if (card.classList.contains('sa-customize-finish-picker__option')) {
              card.classList.add('sa-customize-finish-picker__option--active');
            } else if (card.classList.contains('sa-customize-style-picker__card')) {
              card.classList.add('sa-customize-style-picker__card--active');
            } else {
              card.classList.add('sa-option-card--active');
              if (card.classList.contains('sa-swatch')) card.classList.add('sa-swatch--active');
            }
          } else {
            input.checked = false;
            if (card.classList.contains('sa-customize-finish-picker__option')) {
              card.classList.remove('sa-customize-finish-picker__option--active');
            } else if (card.classList.contains('sa-customize-style-picker__card')) {
              card.classList.remove('sa-customize-style-picker__card--active');
            } else {
              card.classList.remove('sa-option-card--active');
              if (card.classList.contains('sa-swatch')) card.classList.remove('sa-swatch--active');
            }
          }
        }
      });

      // Handle Custom Select Update
      if (type === 'colorType') {
        const selectContainer = this.querySelector('.sa-select-container');
        if (selectContainer) {
          const options = selectContainer.querySelectorAll('.sa-select-option');
          const triggerText = selectContainer.querySelector('.sa-select-text');
          const triggerValue = selectContainer.querySelector('.sa-select-value');

          options.forEach(opt => {
            if (opt.dataset.value === value) {
              opt.classList.add('selected');
              // Update trigger
              const text = opt.querySelector('span').textContent;
              const iconSrc = opt.querySelector('img') ? opt.querySelector('img').src : null;

              if (triggerText) triggerText.textContent = text;

              let triggerIcon = triggerValue.querySelector('.sa-select-icon');
              if (iconSrc) {
                if (!triggerIcon) {
                  triggerIcon = document.createElement('img');
                  triggerIcon.className = 'sa-select-icon';
                  triggerIcon.width = 40;
                  triggerValue.insertBefore(triggerIcon, triggerText);
                }
                triggerIcon.src = iconSrc;
                triggerIcon.alt = text;
              } else if (triggerIcon) {
                triggerIcon.remove();
              }
            } else {
              opt.classList.remove('selected');
            }
          });
        }
      }
    }

    renderColorSlots(colorTypeLabel) {
      if (!this.colorConfigContainer) return;

      // Find the selected type definition
      const typeData = this.productData.customize_color_type.find(t => t.label === colorTypeLabel);
      if (!typeData) {
        this.colorConfigContainer.innerHTML = '';
        return;
      }

      // Determine number of slots based on key prefix
      // e.g. "single_..." -> 1, "two_..." -> 2, "three_..." -> 3
      let slotCount = 1;
      const key = typeData.key || '';
      if (key.startsWith('two')) {
        slotCount = 2;
      } else if (key.startsWith('three')) {
        slotCount = 3;
      }

      let tabsHtml = '<div class="sa-slot-tabs">';
      let palettesHtml = '<div class="sa-palettes">';

      // Loop for each slot
      for (let index = 0; index < slotCount; index++) {
        const slotName = `Color ${index + 1}`;
        const isFirst = index === 0;

        // Get colors for this slot from the corresponding slot data
        // Slot 0 -> customize_color_slots[0]
        let slotColors = this.productData.customize_color_slots && this.productData.customize_color_slots[index]
          ? this.productData.customize_color_slots[index]
          : [];

        // Filter by Finish
        if (this.state.finish) {
          const activeFinish = this.state.finish.trim().toLowerCase();
          slotColors = slotColors.filter(color => {
            const finishes = color.colorful.finishs;
            if (!finishes || !Array.isArray(finishes)) return false;
            return finishes.some(f => f && f.trim().toLowerCase() === activeFinish);
          });
        }

        // 1. Build Tab
        tabsHtml += `
          <div class="sa-slot-tab ${isFirst ? 'sa-slot-tab--active' : ''}" data-tab-index="${index}">
             <div class="sa-tab-preview sa-tab-preview-${index}">
                <img class="sa-tab-preview-img" style="display:none;" />
             </div>
             <div class="sa-tab-info">
               <span class="sa-tab-title">${slotName}</span>
               <span class="sa-tab-subtitle sa-tab-subtitle-${index}">Select Color</span>
             </div>
          </div>
        `;

        // 2. Build Palette (Grid of colors)
        palettesHtml += `
          <div class="sa-palette ${isFirst ? 'sa-palette--active' : ''}" data-palette-index="${index}">
             <div class="sa-color-list">
        `;

        slotColors.forEach(color => {
          const colorValue = color.colorful.color;
          const colorImage = color.colorful.image;
          const colorName = color.name;
          const templateImage = color[`${key}_template_image`] || '';
          const logoName = color.colorful.logo || '';

          palettesHtml += `
              <label class="sa-swatch" title="${colorName}">
                 <input type="radio" name="customize_color_selection_${index}" value="${colorName}" 
                        data-type="colorSelection" 
                        data-slot="Color ${index + 1}"
                        data-index="${index}"
                        data-color-value="${colorValue}"
                        data-color-image="${colorImage}"
                        data-template-image="${templateImage}"
                        data-logo="${logoName}"
                        style="display:none">
                 ${colorImage
              ? `<img src="${colorImage}" class="sa-swatch-img">`
              : `<span class="sa-swatch-color" style="background-color: ${colorValue}"></span>`
            }
              </label>
            `;
        });

        palettesHtml += `
             </div>
          </div>
        `;
      }

      tabsHtml += '</div>';
      palettesHtml += '</div>';

      this.colorConfigContainer.innerHTML = tabsHtml + palettesHtml;

      // Re-bind tabs logic
      this.initTabsLogic();

      // Default selection for each slot: Select based on defaults or fallback to first
      for (let index = 0; index < slotCount; index++) {
        const inputs = this.colorConfigContainer.querySelectorAll(`input[name="customize_color_selection_${index}"]`);
        if (inputs.length === 0) continue;

        let targetInput = inputs[0]; // Fallback to first
        let isRestored = false;

        // 1. Try to restore previous selection
        const slotName = `Color ${index + 1}`;
        const savedVal = this.state.colorSelections[slotName];

        if (savedVal) {
           const match = Array.from(inputs).find(i => i.value === savedVal);
           if (match) {
               targetInput = match;
               isRestored = true;
           }
        }

        // 2. Check for specific default in typeData (if not restored)
        if (!isRestored) {
          const defaultKey = `color_${index + 1}_default`;
          if (typeData[defaultKey]) {
            const defaultName = typeData[defaultKey];
            // Try to match by name (value)
            const match = Array.from(inputs).find(input => input.value === defaultName);
            
            if (match) {
              targetInput = match;
            }
          }
        }

        if (targetInput) {
          // Manually set checked state
          targetInput.checked = true;
          
          // Directly call the selection logic instead of relying on click/change event
          // This ensures the logo auto-selection logic runs reliably during initialization
          const slot = targetInput.dataset.slot;
          const value = targetInput.value;
          this.selectColorForSlot(slot, value, targetInput, isRestored);
        }
      }
    }

    initTabsLogic() {
      const tabs = this.querySelectorAll('.sa-slot-tab');
      const palettes = this.querySelectorAll('.sa-palette');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const index = tab.dataset.tabIndex;

          // Toggle active state
          tabs.forEach(t => t.classList.remove('sa-slot-tab--active'));
          tab.classList.add('sa-slot-tab--active');

          palettes.forEach(p => p.classList.remove('sa-palette--active'));
          const targetPalette = this.querySelector(`.sa-palette[data-palette-index="${index}"]`);
          if (targetPalette) targetPalette.classList.add('sa-palette--active');

          this.updateColorDescription();
        });
      });
    }

    renderLogoColors(logoName) {
      if (!this.logoConfigContainer) return;

      const logoData = this.productData.customize_logo.find(l => l.name === logoName);
      if (!logoData || !logoData.colors) {
        this.logoConfigContainer.innerHTML = '';
        return;
      }

      let html = '<div class="sa-color-list">';
      logoData.colors.forEach(color => {
        const colorValue = color.colorful.color;
        const colorImage = color.colorful.image;
        const colorName = color.name;

        // We need to know which template image to use. 
        // The logic in JSON generation uses 'l1_template_image' etc based on key.
        // We can find the key from logoData.key
        const key = logoData.key || 'l1';
        const templateKey = `${key}_template_image`;
        const templateImage = color[templateKey] || '';

        html += `
              <label class="sa-swatch" title="${colorName}">
                 <input type="radio" name="customize_logo_color" value="${colorName}" 
                        data-type="logoColor" 
                        data-template-image="${templateImage}"
                        data-color-value="${colorValue || ''}"
                        data-color-image="${colorImage || ''}"
                        style="display:none">
                 ${colorImage
            ? `<img src="${colorImage}" class="sa-swatch-img">`
            : `<span class="sa-swatch-color" style="background-color: ${colorValue}"></span>`
          }
              </label>
            `;
      });
      html += '</div>';

      this.logoConfigContainer.innerHTML = html;

      // Auto-select first logo color
      const firstInput = this.logoConfigContainer.querySelector('input[name="customize_logo_color"]');
      if (firstInput) {
        firstInput.parentElement.click();
      }
    }

    async updatePreview() {
      if (!this.previewHandler) {
        const canvasEl = this.querySelector('#sa-preview-canvas');
        const scriptUrl = this.dataset.previewScript;

        if (canvasEl && scriptUrl) {
          try {
            const module = await import(scriptUrl);
            const SaCustomizePreview = module.default;
            this.previewHandler = new SaCustomizePreview(canvasEl, 2960, 2240);
          } catch (e) {
            console.error('Failed to load preview script', e);
            return;
          }
        } else {
          // Script not loaded yet or canvas missing, retry shortly
          if (canvasEl) setTimeout(() => this.updatePreview(), 100);
          return;
        }
      }

      // Gather data
      const data = {
        base: this.productData.baseMap || null,
        watermark: this.productData.watermark || null,
        finish: null,
        colors: [],
        logo: null
      };

      // 1. Finish Layer
      if (this.state.finish && this.productData.customize_finish) {
        const f = this.productData.customize_finish.find(i => i.name === this.state.finish);
        if (f && f.template_image) data.finish = f.template_image;
      }

      // 2. Color Layers
      const slots = Object.keys(this.state.colorSelections).sort();
      slots.forEach(slot => {
        const colorName = this.state.colorSelections[slot];
        const input = this.querySelector(`input[data-slot="${slot}"][value="${colorName}"]`);
        if (input && input.dataset.templateImage) {
          data.colors.push(input.dataset.templateImage);
          
          if (!data.colorDetails) data.colorDetails = [];
          data.colorDetails.push({
            name: colorName,
            colorImage: input.dataset.colorImage || '',
            colorValue: input.dataset.colorValue || '',
            slot: slot
          });
        }
      });

      // 3. Logo Layer
      if (this.state.logoColor) {
        const input = this.querySelector(`input[data-type="logoColor"][value="${this.state.logoColor}"]`);
        if (input && input.dataset.templateImage) {
          data.logo = input.dataset.templateImage;

          data.logoDetails = {
            name: this.state.logoColor,
            colorImage: input.dataset.colorImage || '',
            colorValue: input.dataset.colorValue || ''
          };
        }
      }

      this.previewHandler.update(data);
    }

    dataURLtoBlob(dataurl) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    }

    async getUninterceptedFetch() {
      // 1. Try to get native fetch from iframe to bypass global interceptors
      if (this._safeFetch) return this._safeFetch;

      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        // Must append to document to initialize contentWindow
        document.body.appendChild(iframe);
        
        // Small delay to ensure ready
        await new Promise(resolve => setTimeout(resolve, 0));
        
        if (iframe.contentWindow && iframe.contentWindow.fetch) {
           this._safeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);
           // We keep the iframe in DOM to ensure the fetch function remains valid
           // document.body.removeChild(iframe); 
        } else {
           // Fallback
           this._safeFetch = window.fetch;
        }
      } catch (e) {
        console.error('Failed to create safe fetch, falling back to window.fetch', e);
        this._safeFetch = window.fetch;
      }

      return this._safeFetch;
    }

    toggleGlobalLoading(show) {
      const loader = document.querySelector('.m-page-transition');
      if (loader) {
        if (show) {
          loader.classList.remove('fade-out');
          loader.classList.add('fade-in');
          // Ensure it is visible if using visibility: hidden
          loader.style.visibility = 'visible'; 
          loader.style.opacity = '1';
          loader.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
        } else {
          loader.classList.remove('fade-in');
          loader.classList.add('fade-out');
           // Reset inline styles to let class take over or just hide it
           loader.style.visibility = '';
           loader.style.opacity = '';
           loader.style.backgroundColor = '';
        }
      }
    }

    initModal() {
      if (this.modal) return;
      this.modal = this.querySelector('#sa-finish-warning-modal');
      if (!this.modal) return;

      const closeBtn = this.modal.querySelector('.sa-modal-close');
      const cancelBtn = this.modal.querySelector('.sa-modal-cancel');
      const confirmBtn = this.modal.querySelector('.sa-modal-confirm');
      const backdrop = this.modal.querySelector('.sa-modal-backdrop');

      const hide = () => this.hideFinishWarningModal();

      if (closeBtn) closeBtn.addEventListener('click', hide);
      if (cancelBtn) cancelBtn.addEventListener('click', hide);
      if (backdrop) backdrop.addEventListener('click', hide);
      
      if (confirmBtn) {
        // Clone to prevent multiple listeners if re-initialized
        const newConfirm = confirmBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);
        newConfirm.addEventListener('click', () => {
          if (this.pendingFinish) {
             this.selectOption('finish', this.pendingFinish);
          }
          this.hideFinishWarningModal();
        });
      }
    }

    checkFinishAvailability(finishValue) {
      const unavailable = [];
      const finish = finishValue ? finishValue.trim().toLowerCase() : '';
      if (!finish) return unavailable;

      if (!this.state.colorSelections) return unavailable;

      Object.keys(this.state.colorSelections).forEach(slot => {
        const colorName = this.state.colorSelections[slot];
        if (!colorName) return;

        const index = parseInt(slot.replace('Color ', '')) - 1;
        if (isNaN(index)) return;

        const slotColors = this.productData.customize_color_slots && this.productData.customize_color_slots[index];
        if (!slotColors) return;

        const colorData = slotColors.find(c => c.name === colorName);
        if (!colorData) return;

        if (!colorData.colorful) return; // Safety check

        const finishes = colorData.colorful.finishs;
        if (!finishes || !Array.isArray(finishes)) {
             // If finishes is undefined/null, assume it's NOT available for safety, or check logic
             unavailable.push({ slot: slot, name: colorName });
             return;
        }

        const isAvailable = finishes.some(f => f && f.trim().toLowerCase() === finish);
        if (!isAvailable) {
          unavailable.push({ slot: slot, name: colorName });
        }
      });

      return unavailable;
    }

    showFinishWarningModal(unavailable, finishValue) {
      if (!this.modal) {
        this.initModal();
      }

      if (!this.modal) {
        // Fallback if modal DOM is missing
        if (confirm(`These colors are not available in ${finishValue} finish:\n\n${unavailable.map(i => `- ${i.slot}: ${i.name}`).join('\n')}\n\nDo you want to change to ${finishValue} finish?`)) {
            this.selectOption('finish', finishValue);
        } else {
            this.hideFinishWarningModal();
        }
        return;
      }

      this.pendingFinish = finishValue;

      // Update Text
      const names = this.modal.querySelectorAll('.sa-modal-finish-name');
      names.forEach(el => el.textContent = finishValue);

      // Update List
      const list = this.modal.querySelector('.sa-modal-list');
      if (list) {
        list.innerHTML = unavailable.map(item => `<li>${item.slot}: ${item.name}</li>`).join('');
      }

      // Show
      this.modal.classList.remove('hidden');
      // Force reflow
      void this.modal.offsetWidth; 
      this.modal.classList.add('open');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    hideFinishWarningModal() {
      if (!this.modal) return;
      this.modal.classList.remove('open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Revert UI to match current state
      if (this.state.finish) {
         const currentInput = this.querySelector(`input[name="customize_finish"][value="${this.state.finish}"]`);
         if (currentInput) currentInput.checked = true;
      }
      
      this.pendingFinish = null;
    }

    async handleAddToCart() {
      if (!this.state.parentVariantId) {
        alert('Please select a frame product first.');
        return;
      }

      if (this.productData.customizable && !this.state.file) {
        alert('Please upload a paint reference file.');
        return;
      }

      this.addToCartBtn.classList.add('loading');
      this.addToCartBtn.disabled = true;
      this.toggleGlobalLoading(true);

      const timestamp = Date.now();
      const groupId = `${this.state.parentVariantId}_${this.productData.variantId}_${timestamp}`;
      const gpoGroup = timestamp; // Use timestamp for GPO group

      const colorValues = [];
      // Sort keys to ensure order Color 1, Color 2...
      const slots = Object.keys(this.state.colorSelections).sort();
      slots.forEach(slot => {
        const colorName = this.state.colorSelections[slot];
        colorValues.push(colorName);
      });

      // final_image is the base64 encoded image of the preview canvas
      let finalImage = '';
      if (this.previewHandler) {
        finalImage = await this.previewHandler.getSnapshotUrl();

        // TODO:  Download image
        // if (finalImage) {
        //   const link = document.createElement('a');
        //   link.download = `custom-preview-${Date.now()}.png`;
        //   link.href = finalImage;
        //   document.body.appendChild(link);
        //   link.click();
        //   document.body.removeChild(link);
        // }
      }

      // 1. Prepare FormData for the item with the image (Customize Frame)
      const formData = new FormData();
      formData.append('id', this.productData.variantId);
      formData.append('quantity', 1);
      formData.append('properties[_group_id]', groupId);
      formData.append('properties[_type]', 'customize_frame');
      formData.append('properties[Customize finish]', this.state.finish);
      formData.append('properties[Customize color type]', this.state.colorType || '');
      formData.append('properties[Customize color]', colorValues.join(', '));
      formData.append('properties[Customize logo]', this.state.logo);
      formData.append('properties[Customize logo color]', this.state.logoColor || '');

      // Add new properties
      if (this.state.file) {
        formData.append('properties[Customize references]', this.state.file);
      }
      if (this.state.description) {
        formData.append('properties[Customize description]', this.state.description);
      }

      // Convert Base64 to Blob and append as file
      if (finalImage) {
        const imageBlob = this.dataURLtoBlob(finalImage);
        formData.append('properties[Customize final image]', imageBlob, 'preview.webp');
      }

      // Use a safe fetch to bypass any global interceptors (like side-cart apps) that might force JSON
      const safeFetch = await this.getUninterceptedFetch();
      const url = window.Shopify.routes.root + 'cart/add.js?_t=' + Date.now();

      // Step 1: Add Customize Product (The one with the image)
      safeFetch(url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
      .then(response => {
        if (response.status && response.status !== 200) {
           throw new Error('Failed to add custom item');
        }
        return response.json();
      })
        .then(response => {
          // Step 2 & 3: Add Addon Items and then Parent Item
          // Order requested: Customize Product -> Addon Items -> Main Product
          
          const itemsToAdd = [];

          // 2. Add addon items
          if (this.state.addonItems && this.state.addonItems.length > 0) {
             this.state.addonItems.forEach(addon => {
                 if (addon && addon.id) {
                     itemsToAdd.push({
                         id: parseInt(addon.id),
                         quantity: 1,
                         properties: {
                             '_group_id': groupId, // Maintain legacy group ID for cart sync
                             '_gpo_product_group': gpoGroup,
                             '_gpo_field_name': addon.name,
                             '_type': 'addon'
                         }
                     });
                 }
             });
          }

          // 3. Add the parent item
          const parentItem = {
            id: parseInt(this.state.parentVariantId),
            quantity: 1,
            properties: {
              '_group_id': groupId, // Maintain legacy group ID for cart sync
              '_gpo_product_group': gpoGroup,
              '_type': 'parent',
              ...this.state.extraProperties // Spread extra properties
            }
          };
          itemsToAdd.push(parentItem);

          return fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: itemsToAdd })
          });
        })
        .then(response => response.json())
        .then(data => {
          window.location.href = '/cart';
        })
        .catch((error) => {
          console.error('Error:', error);
        })
        .finally(() => {
          this.addToCartBtn.classList.remove('loading');
          this.addToCartBtn.disabled = false;
          this.toggleGlobalLoading(false);
        });
    }
  }

  customElements.define('sa-customize-main', SaCustomizeMain);
}
